/**
 * The small honest parts every board cell is made of.
 *
 *   StatusBar    the board's word FILLED with its Monday colour, as Monday draws
 *                it (FilledStatusChip: the ink is chosen per fill for contrast,
 *                bold where neither ink reaches 4.5:1). The word is always on
 *                the chip. A colour the server does not hold is drawn hatched
 *                and said in words.
 *   NotRecorded  "not recorded", never 0 and never a bare dash, with the
 *                reason available to a screen reader.
 *   SourceTag    who wrote the value: "board" (the Monday mirror) or "eMerge".
 */
import React from 'react';
import type { CellSource } from './types';
import FilledStatusChip from '../status/FilledStatusChip';

export const NOT_RECORDED = 'not recorded';

export function NotRecorded({ why, label = NOT_RECORDED }: { why?: string; label?: string }) {
  return (
    <span className="board-na" title={why}>
      {label}
      {why ? <span className="board-sr"> — {why}</span> : null}
    </span>
  );
}

export function SourceTag({ source, detail }: { source: CellSource; detail?: string }) {
  const word = source === 'EMERGE' ? 'eMerge' : 'board';
  return (
    <span className={`board-src board-src--${source === 'EMERGE' ? 'emerge' : 'board'}`}>
      {word}{detail ? ` · ${detail}` : ''}
    </span>
  );
}

export function StatusBar({ word, color, note, emptyLabel = 'No status' }: { word: string | null | undefined; color: string | null | undefined; note?: string | null; emptyLabel?: string }) {
  if (!word) {
    return <span className="board-status board-status--empty"><span className="board-status__word">{emptyLabel}</span></span>;
  }
  const uncoloured = !color;
  if (!uncoloured) {
    return <FilledStatusChip word={word} hex={color} className="board-status-chip" />;
  }
  return (
    <span
      className={`board-status${uncoloured ? ' board-status--uncoloured' : ''}`}
      style={uncoloured ? undefined : ({ '--board-bar': color } as React.CSSProperties)}
      title={uncoloured ? (note || 'eMerge does not hold this word\'s Monday colour.') : undefined}
    >
      <span className="board-status__word">{word}</span>
      {uncoloured && <span className="board-sr"> (colour not recorded)</span>}
    </span>
  );
}

/** A dated value with its source on a second line; tentative says so in words. */
export function DatedCell({ on, source, what, tentative = false, none }: { on: string | null | undefined; source: CellSource; what?: string; tentative?: boolean; none?: React.ReactNode }) {
  if (!on) return <>{none ?? <NotRecorded />}</>;
  return (
    <span className="board-dated">
      <span className="board-dated__value">{on}{tentative ? <span className="board-tentative"> tentative</span> : null}</span>
      <SourceTag source={source} detail={what} />
    </span>
  );
}
